'use strict';

/**
 * Originally written by Marc Jenkins as a single use script.
 * Turning into re-usable module by Hiren Patel.
 *
 * DO NOT EDIT THIS FILE.
 * Try configuring the behaviour by providing the options in JS or
 * by data attributes in HTML.
 * Speak to Hiren if you _really_ need a feature adding.
 *
 * Behaviour can be configured either by passing an object overriding
 * the following defaults:
 *
 * itemSelector: CSS selector for each accordion item; .accordion__item
 * handleSelector: CSS selector for the click handle; .accordion__handle
 * contentSelector: CSS selector for the body being toggled; .accordion__content
 * activeClass: CSS class name to flag an item as open or closed; accordion__item--active
 * allowMultiple: allow multiple items to be opened at the same time; false
 * openByDefault: choose to open items by default (first, all, none); none
 * scrollToItem: bring the item into view after opening; true
 * slideSpeed: slide animation speed; 500
 * slideEasing: CSS easing function for slide animation; ease-in-out
 * onOpen: callback function to call after opening; null
 * onClose: callback function to call after closing; null
 *
 * Or by passing the following data attributes directly on the container:
 *
 * data-accordion-item-selector
 * data-accordion-handle-Selector
 * data-accordion-content-selector
 * data-accordion-active-class
 * data-accordion-allow-multiple
 * data-accordion-open-by-default
 * data-accordion-scroll-to-item
 * data-accordion-slide-speed
 * data-accordion-slide-easing
 */

import 'dom-slider';
import { extend } from 'lodash';
import parseBoolean from 'utils/parse-boolean';

function initialise(container, options = options || {}) {
    options = extend({}, {
        itemSelector: options.itemSelector || container.dataset.accordionItemSelector || '.accordion__item',
        handleSelector: options.handleSelector || container.dataset.accordionHandleSelector || '.accordion__handle',
        contentSelector: options.contentSelector || container.dataset.accordionContentSelector || '.accordion__content',
        activeClass: options.activeClass || container.dataset.accordionActiveClass || 'accordion__item--active',
        allowMultiple: parseBoolean(options.allowMultiple || container.dataset.accordionAllowMultiple, false),
        openByDefault: (options.openByDefault || container.dataset.accordionOpenByDefault || 'none').toLowerCase(),
        scrollToItem: parseBoolean(options.scrollToItem || container.dataset.accordionScrollToItem, true),
        slideSpeed: options.slideSpeed || container.dataset.accordionSlideSpeed || 500,
        slideEasing: options.slideEasing || container.dataset.accordionSlideEasing || 'ease-in-out',
        onOpen: options.onOpen || null,
        onClose: options.onClose || null,
    });

    const entries = container.querySelectorAll(options.itemSelector);

    if (!entries.length) {
        return;
    }

    let loading = true;

    closeAll().then(() => {
        start().then(() => {
            loading = false;
        });
    });

    function start() {
        entries.forEach(entry => {
            entry.querySelector(options.handleSelector).addEventListener('click', () => {
                if (entry.dataset.processing === true) {
                    return;
                }

                if (entry.classList.contains(options.activeClass)) {
                    close(entry, true);
                } else {
                    open(entry, true);
                }
            }, false);
        });

        return [...entries].reduce((carry, entry, index) => {
            return carry.then(() => {
                if (options.openByDefault === 'all') {
                    return open(entry);
                }

                if (options.openByDefault === 'first' && index === 0) {
                    return open(entry);
                }

                return Promise.resolve();
            });
        }, Promise.resolve());
    }

    function open(item, clicked = false) {
        let promise = Promise.resolve();

        if (loading === false && options.allowMultiple === false) {
            promise = closeAll();
        }

        return promise.then(() => {
            const handle = item.querySelector(options.handleSelector);
            const content = item.querySelector(options.contentSelector);

            handle.setAttribute('aria-expanded', 'true');
            content.setAttribute('aria-hidden', 'false');

            item.dataset.processing = true;

            item.classList.add(options.activeClass);

            return content.slideDown(loading === true ? 0 : options.slideSpeed, options.slideEasing).then(() => {
                scrollTo(item);

                if (clicked === true && typeof options.onOpen === 'function') {
                    options.onOpen.call(this, item, handle, content);
                }

                item.dataset.processing = false;
            });
        });
    }

    function close(item, clicked = false) {
        const handle = item.querySelector(options.handleSelector);
        const content = item.querySelector(options.contentSelector);

        handle.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');

        item.dataset.processing = true;

        item.classList.remove(options.activeClass);

        return content.slideUp(loading === true ? 0 : options.slideSpeed, options.slideEasing).then(() => {
            if (clicked === true && typeof options.onClose === 'function') {
                options.onClose.call(this, item, handle, content);
            }

            item.dataset.processing = false;
        });
    }

    function closeAll() {
        const promises = [];

        entries.forEach(entry => {
            promises.push(close(entry));
        });

        return Promise.all(promises);
    }

    function scrollTo(item) {
        if (loading === true || options.scrollToItem === false) {
            return;
        }

        item.scrollIntoView({
            behavior: 'smooth',
        });
    }
}

export default function accordion(selectorOrContainer, options) {
    if (selectorOrContainer instanceof Element) {
        return initialise(selectorOrContainer, options);
    }

    selectorOrContainer = selectorOrContainer || '.accordion';

    document.querySelectorAll(selectorOrContainer).forEach(element => {
        initialise(element, options);
    });
}
